/**
 * Slash command suggestion extension for the tracker editor.
 *
 * Wires TipTap's Suggestion plugin to listen for "/" at the start of an
 * empty (or whitespace-only) line and forward the query to a render
 * callback that owns the popover UI. Selecting an item replaces the
 * triggering "/…" range with the chosen block.
 *
 * The popover itself (positioning, scrolling list, keyboard handling)
 * lives in TipTapEditor.tsx so the extension stays UI-agnostic.
 */

import { Extension, type Range } from "@tiptap/core";
import Suggestion, {
  type SuggestionOptions,
  type SuggestionProps,
} from "@tiptap/suggestion";
import type { Editor } from "@tiptap/react";

export interface SlashCommandItem {
  title: string;
  description?: string;
  command: (args: { editor: Editor; range: Range }) => void;
}

export type SlashSuggestionProps = SuggestionProps<SlashCommandItem>;

export interface SlashCommandOptions {
  suggestion: Omit<SuggestionOptions<SlashCommandItem>, "editor">;
}

export const SlashCommand = Extension.create<SlashCommandOptions>({
  name: "slashCommand",

  addOptions() {
    return {
      suggestion: {
        char: "/",
        // Only fire at the start of an empty line so it doesn't hijack
        // mid-sentence slashes ("and/or", URLs, etc.).
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from);
          const lineStart = $from.start();
          const before = state.doc.textBetween(lineStart, range.from, "\n", "");
          return before.trim() === "";
        },
        command: ({ editor, range, props }) => {
          props.command({ editor: editor as Editor, range });
        },
        items: () => [],
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion<SlashCommandItem>({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});
